import { useTranslation } from 'react-i18next';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import PlanCardDate from '@/components/Cards/PlanCard//PlanCardComponents/PlanCardDate';
import { printPlan } from '@/components/print-report';
import { PrintCertificates } from '@/components/PrintCertificates';
import { PrintReportButton } from '@/components/PrintReportButton';

import { routes } from 'constants/index';

import PlanCard from '../PlanCard';
import PlanCardLabel from '../PlanCardComponents/PlanCardLabel';
import PlanCardSubHeader from '../PlanCardComponents/PlanCardSubHeader';
import PlanCardTitle from '../PlanCardComponents/PlanCardTitle';
import PlanCardContent from '../PlanCardContent/PlanCardContent';

import { PlanReport } from '@/_generated/api-clients/xp';

interface ReportsPlanCardProps {
  assignedUsers: number;
  completedUsers: number;
  plan: PlanReport;
  totalSteps?: number;
}

const ReportsPlanCard: React.FC<ReportsPlanCardProps> = ({
  assignedUsers,
  completedUsers,
  plan,
  totalSteps,
}) => {
  const { t } = useTranslation();

  const percentComplete = (completedUsers / assignedUsers) * 100;

  const renderPlanStatus = () => {
    if (!!assignedUsers && completedUsers === assignedUsers) {
      return (
        <PlanCardSubHeader className="success" data-testid="PlanTaskCount">
          {t('Generic.completedByAll')}
        </PlanCardSubHeader>
      );
    }

    const teamMemberStatus = assignedUsers
      ? `${completedUsers}/${assignedUsers} ${t(
          'Generic.teamMembersCompleted',
        )}`
      : t('Generic.noTeamMembersAssigned');

    return (
      <PlanCardSubHeader data-testid="PlanTaskCount">
        {totalSteps === 0 ? t('Generic.noContentInPlan') : teamMemberStatus}
      </PlanCardSubHeader>
    );
  };

  return (
    <PlanCard
      buttons={
        <div className="flex items-center gap-4">
          <PrintCertificates planId={plan.id} />
          <PrintReportButton
            onClick={e => {
              e.preventDefault();
              printPlan({
                ...plan,
                name: plan.name ?? {},
                type: plan.type ?? 'STORE',
              });
            }}
          />
        </div>
      }
      category={plan.category}
      dataTestId={'ReportsPlanCard'}
      link={routes.reports.planProgress({ planId: plan.id })}
      progress={assignedUsers && totalSteps !== 0 ? percentComplete : null}
    >
      <PlanCardContent>
        <PlanCardLabel>
          {t(mapCategoryToAliasTranslation(plan.category))}
        </PlanCardLabel>
        <div className="flex flex-col gap-0 sm:flex-row sm:gap-2">
          <PlanCardTitle>{itemByLanguage(plan.name)}</PlanCardTitle>
        </div>
        {renderPlanStatus()}
      </PlanCardContent>
      {!plan.readonly && plan.created && <PlanCardDate date={plan.created} />}
    </PlanCard>
  );
};

export default ReportsPlanCard;
